import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { MessageSquare, Send, Shield, User, Loader2, AlertCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  open: { label: 'Ouvert', cls: 'bg-yellow-500/15 text-yellow-400' },
  in_progress: { label: 'En cours', cls: 'bg-orange-500/15 text-orange-400' },
  answered: { label: 'Répondu', cls: 'bg-green-500/15 text-green-400' },
  closed: { label: 'Fermé', cls: 'bg-blue-500/15 text-blue-300' },
};

export default function TicketViewPage() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [replySuccess, setReplySuccess] = useState(false);
  const messagesEndRef = useRef(null);

  const loadTicket = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets/${ticketId}`);
      setTicket(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.status === 404 ? 'Ticket introuvable.' : 'Erreur lors du chargement du ticket.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTicket(); }, [ticketId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages]);

  const handleReply = async () => {
    const msg = replyMessage.trim();
    if (!msg) return;
    setSending(true);
    setReplySuccess(false);
    try {
      await axios.post(`${API}/support/tickets/${ticketId}/reply`, { message: msg });
      setReplyMessage('');
      setReplySuccess(true);
      await loadTicket();
      setTimeout(() => setReplySuccess(false), 3000);
    } catch {
      setError('Erreur lors de l\'envoi de votre réponse.');
    } finally {
      setSending(false);
    }
  };

  const statusInfo = ticket ? (STATUS_MAP[ticket.status] || STATUS_MAP.open) : STATUS_MAP.open;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col">
      <NavigationMenu />
      <main className="flex-1 pt-24 pb-12 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : error && !ticket ? (
            <div className="bg-slate-800/50 border border-red-500/20 rounded-2xl p-8 text-center" data-testid="ticket-error">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-300 text-lg font-medium">{error}</p>
              <p className="text-blue-300/50 text-sm mt-2">Vérifiez le lien ou contactez le support.</p>
            </div>
          ) : ticket && (
            <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden flex flex-col" data-testid="ticket-view-container">
              {/* Header */}
              <div className="p-5 border-b border-blue-500/20">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h1 className="text-xl font-bold text-white flex items-center gap-2" data-testid="ticket-subject">
                      <MessageSquare className="w-5 h-5 text-emerald-400" />
                      {ticket.subject}
                    </h1>
                    <p className="text-blue-300/50 text-sm mt-1">
                      Ticket <span className="font-mono text-blue-400">#{ticket.ticket_id}</span>
                      {' — '}{ticket.name || ticket.email}
                    </p>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusInfo.cls}`} data-testid="ticket-status">
                    {statusInfo.label}
                  </span>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4 max-h-[500px]" data-testid="ticket-messages">
                {(ticket.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.sender === 'admin' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[80%] rounded-2xl p-4 ${
                      m.sender === 'admin'
                        ? 'bg-emerald-500/10 border border-emerald-500/20 rounded-tl-md'
                        : 'bg-blue-500/10 border border-blue-500/20 rounded-tr-md'
                    }`} data-testid={`ticket-msg-${i}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        {m.sender === 'admin'
                          ? <Shield className="w-3.5 h-3.5 text-emerald-400" />
                          : <User className="w-3.5 h-3.5 text-blue-400" />
                        }
                        <span className={`text-xs font-medium ${m.sender === 'admin' ? 'text-emerald-400' : 'text-blue-400'}`}>
                          {m.sender === 'admin' ? 'Support SENDBOX' : (ticket.name || ticket.email)}
                        </span>
                        <span className="text-[10px] text-blue-300/30 flex items-center gap-0.5">
                          <Clock className="w-2.5 h-2.5" />
                          {m.created_at ? new Date(m.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{m.message}</p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply area */}
              {ticket.status !== 'closed' ? (
                <div className="border-t border-blue-500/20 p-4" data-testid="ticket-reply-area">
                  {replySuccess && (
                    <div className="mb-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-2 text-emerald-300 text-sm" data-testid="reply-success-msg">
                      Votre réponse a été envoyée avec succès.
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={replyMessage}
                      onChange={e => setReplyMessage(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleReply()}
                      placeholder="Écrire votre réponse..."
                      className="flex-1 bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 placeholder-blue-300/40"
                      data-testid="ticket-reply-input"
                    />
                    <Button
                      onClick={handleReply}
                      disabled={sending || !replyMessage.trim()}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-5"
                      data-testid="ticket-reply-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border-t border-blue-500/20 p-4 text-center" data-testid="ticket-closed-notice">
                  <p className="text-blue-300/50 text-sm">Ce ticket est fermé. Si vous avez besoin d'aide supplémentaire, veuillez ouvrir un nouveau ticket via la page <a href="/support" className="text-blue-400 underline">Support</a>.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <DynamicFooter />
    </div>
  );
}
